import type { Identifier } from "ra-core";

import type { Colega, Deal, LeadSource } from "../types";

/**
 * Almikia Epic E2b (TASK-023): pure rules behind the lead form's source tag
 * and colega fields, the leads CSV export and the leads CSV import.
 */

/**
 * The source picker's choices: every active source plus the lead's own
 * current source even when it is inactive, so an existing lead never loses
 * its tag on edit. Never mutates `sources`.
 */
export function buildLeadSourceChoices(
  sources: readonly LeadSource[],
  currentId: Identifier | null | undefined,
): LeadSource[] {
  return sources
    .filter((source) => source.active || source.id === currentId)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

/**
 * Save-time rule: a lead whose type is not 'colega' never keeps a
 * `colega_id`. Returns a new object; the legacy `source` and
 * `external_broker` texts are never touched.
 */
export function clearColegaUnlessColega<T extends Partial<Deal>>(data: T): T {
  if (data.lead_type === "colega") return data;
  if (data.colega_id == null) return data;
  return { ...data, colega_id: null };
}

const normalizeText = (text: string): string =>
  text.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Importer matching: a CSV `source` text against the ACTIVE sources,
 * case-insensitive and whitespace-tolerant. `undefined` when unmatched.
 */
export function matchLeadSource(
  sources: readonly LeadSource[],
  text: string | undefined,
): LeadSource | undefined {
  if (text === undefined) return undefined;
  const wanted = normalizeText(text);
  if (wanted.length === 0) return undefined;
  return sources.find(
    (source) => source.active && normalizeText(source.name) === wanted,
  );
}

/** Extra leads CSV columns: the source tag name and the colega name/inmobiliaria. */
export function mapLeadSourceColegaToCsvRow(
  deal: Pick<Deal, "lead_source_id" | "colega_id">,
  sources: readonly LeadSource[],
  colegas: readonly Colega[],
): {
  lead_source_name: string;
  colega_name: string;
  colega_brokerage: string;
} {
  const source = sources.find(
    (candidate) => String(candidate.id) === String(deal.lead_source_id),
  );
  const colega = colegas.find(
    (candidate) => String(candidate.id) === String(deal.colega_id),
  );
  return {
    lead_source_name: source?.name ?? "",
    colega_name: colega?.full_name ?? "",
    colega_brokerage: colega?.brokerage ?? "",
  };
}
